'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import Link from 'next/link';
import { submissionService, RoastSubmission } from '@/lib/services/submissionService';
import { CATEGORIES } from '@/lib/roastData';
import { Flame, Shield, CheckCircle, XCircle, Trash2, RefreshCw, Search, ChevronDown, Loader2, Image as ImageIcon, BarChart3, Clock, Filter, Check } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';

type StatusFilter = 'all' | 'pending' | 'approved' | 'rejected';

const STATUS_COLORS: Record<string, { bg: string; text: string; border: string }> = {
  pending: { bg: 'rgba(245,158,11,0.1)', text: '#f59e0b', border: 'rgba(245,158,11,0.3)' },
  approved: { bg: 'rgba(34,197,94,0.1)', text: '#22c55e', border: 'rgba(34,197,94,0.3)' },
  rejected: { bg: 'rgba(239,68,68,0.1)', text: '#ef4444', border: 'rgba(239,68,68,0.3)' },
};

export default function AdminPage() {
  const { t } = useLanguage();
  const { user, loading: authLoading } = useAuth();
  const router = useRouter();

  const isAdmin = !!user && user.user_metadata?.full_name?.toLowerCase() === 'rysias';

  useEffect(() => {
    if (!authLoading && !isAdmin) {
      router.replace('/');
    }
  }, [authLoading, isAdmin, router]);

  const [submissions, setSubmissions] = useState<RoastSubmission[]>([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [adminNotes, setAdminNotes] = useState<Record<string, string>>({});
  const [bulkSelected, setBulkSelected] = useState<Set<string>>(new Set());
  const [bulkLoading, setBulkLoading] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const [sortOrder, setSortOrder] = useState<'newest' | 'oldest'>('newest');

  const loadSubmissions = useCallback(async () => {
    setDataLoading(true);
    const data = await submissionService.getAllSubmissions();
    setSubmissions(data);
    setDataLoading(false);
  }, []);

  useEffect(() => {
    loadSubmissions();
  }, [loadSubmissions]);

  const handleUpdateStatus = async (id: string, status: 'approved' | 'rejected') => {
    setActionLoading(id + status);
    const notes = adminNotes[id] || undefined;
    const result = await submissionService.updateSubmissionStatus(id, status, notes);
    if (result.success) {
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status, adminNotes: notes, reviewedAt: new Date().toISOString() } : s));
    }
    setActionLoading(null);
  };

  const handleDelete = async (id: string) => {
    if (!confirm(t('admin_delete_confirm'))) return;
    setActionLoading(id + 'delete');
    const result = await submissionService.deleteSubmission(id);
    if (result.success) {
      setSubmissions(prev => prev.filter(s => s.id !== id));
    }
    setActionLoading(null);
  };

  const filtered = submissions.filter(s => {
    const matchStatus = statusFilter === 'all' || s.status === statusFilter;
    const matchCategory = categoryFilter === 'all' || s.category === categoryFilter;
    const q = searchQuery.toLowerCase();
    const matchSearch = !q || s.roastText?.toLowerCase().includes(q) || s.country.toLowerCase().includes(q) || s.category.toLowerCase().includes(q);
    return matchStatus && matchCategory && matchSearch;
  }).sort((a, b) => {
    const da = new Date(a.submittedAt).getTime();
    const db = new Date(b.submittedAt).getTime();
    return sortOrder === 'newest' ? db - da : da - db;
  });

  const counts = {
    all: submissions.length,
    pending: submissions.filter(s => s.status === 'pending').length,
    approved: submissions.filter(s => s.status === 'approved').length,
    rejected: submissions.filter(s => s.status === 'rejected').length,
  };

  // All-time stats
  const allTimeStats = useMemo(() => {
    const total = submissions.length;
    const approved = submissions.filter(s => s.status === 'approved').length;
    const rejected = submissions.filter(s => s.status === 'rejected').length;
    const pending = submissions.filter(s => s.status === 'pending').length;
    const approvalRate = total > 0 ? Math.round((approved / total) * 100) : 0;

    const countryCounts: Record<string, number> = {};
    const catCounts: Record<string, number> = {};
    submissions.forEach(s => {
      if (s.country) countryCounts[s.country] = (countryCounts[s.country] || 0) + 1;
      if (s.category) catCounts[s.category] = (catCounts[s.category] || 0) + 1;
    });
    const topCountry = Object.entries(countryCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '—';
    const topCatId = Object.entries(catCounts).sort((a, b) => b[1] - a[1])[0]?.[0];
    const topCategory = CATEGORIES.find(c => c.id === topCatId)?.label || topCatId || '—';

    return { total, approved, rejected, pending, approvalRate, topCountry, topCategory };
  }, [submissions]);

  const handleBulkApprove = async () => {
    if (bulkSelected.size === 0) return;
    setBulkLoading(true);
    for (const id of Array.from(bulkSelected)) {
      await submissionService.updateSubmissionStatus(id, 'approved');
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: 'approved' } : s));
    }
    setBulkSelected(new Set());
    setBulkLoading(false);
  };

  const handleBulkReject = async () => {
    if (bulkSelected.size === 0) return;
    setBulkLoading(true);
    for (const id of Array.from(bulkSelected)) {
      await submissionService.updateSubmissionStatus(id, 'rejected');
      setSubmissions(prev => prev.map(s => s.id === id ? { ...s, status: 'rejected' } : s));
    }
    setBulkSelected(new Set());
    setBulkLoading(false);
  };

  const toggleBulkSelect = (id: string) => {
    setBulkSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    if (bulkSelected.size === filtered.length) {
      setBulkSelected(new Set());
    } else {
      setBulkSelected(new Set(filtered.map(s => s.id)));
    }
  };

  if (authLoading || !isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--background)' }}>
        <div className="text-center">
          <Shield size={40} className="mx-auto mb-3" style={{ color: 'var(--primary)' }} />
          <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
            {authLoading ? 'Checking access...' : 'Access denied. Redirecting...'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--background)' }}>
      <header className="sticky top-0 z-50 w-full" style={{ background: 'rgba(10,10,15,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid var(--border)' }}>
        <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #16a34a, #22c55e)' }}>
                <Shield size={16} className="text-white" />
              </div>
              <div>
                <span className="font-bold text-base" style={{ color: 'var(--foreground)' }}>{t('admin_title')}</span>
                <span className="ml-2 text-xs px-2 py-0.5 rounded-full font-semibold" style={{ background: 'rgba(34,197,94,0.1)', color: 'var(--primary)', border: '1px solid rgba(34,197,94,0.2)' }}>
                  RoastRank
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Link href="/" className="flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-semibold transition-all duration-200 hover:bg-white/5"
                style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
                ← Back to Site
              </Link>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-screen-xl mx-auto px-4 sm:px-6 lg:px-8 py-8">

        {/* All-Time Stats */}
        <div className="card-glass p-5 rounded-2xl mb-8" style={{ border: '1px solid rgba(34,197,94,0.15)' }}>
          <div className="flex items-center gap-2 mb-4">
            <BarChart3 size={18} style={{ color: 'var(--primary)' }} />
            <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>{t('admin_alltime_stats')}</h2>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {[
              { label: t('admin_total_submissions'), value: allTimeStats.total, color: 'var(--primary)' },
              { label: t('admin_total_approved'), value: allTimeStats.approved, color: '#22c55e' },
              { label: t('admin_total_rejected'), value: allTimeStats.rejected, color: '#ef4444' },
              { label: t('admin_total_pending'), value: allTimeStats.pending, color: '#f59e0b' },
              { label: t('admin_approval_rate'), value: `${allTimeStats.approvalRate}%`, color: 'var(--primary)' },
              { label: t('admin_top_country'), value: allTimeStats.topCountry, color: 'var(--foreground)', small: true },
              { label: t('admin_top_category'), value: allTimeStats.topCategory, color: 'var(--foreground)', small: true },
            ].map((stat, i) => (
              <div key={i} className="p-3 rounded-xl" style={{ background: 'var(--background)', border: '1px solid var(--border)' }}>
                <div className={`font-bold mb-0.5 ${stat.small ? 'text-sm' : 'text-xl'}`} style={{ color: stat.color }}>
                  {stat.value}
                </div>
                <div className="text-xs" style={{ color: 'var(--muted-foreground)' }}>{stat.label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Status Filter Tabs */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-8">
          {(['all', 'pending', 'approved', 'rejected'] as StatusFilter[]).map(s => (
            <button key={s} onClick={() => setStatusFilter(s)}
              className="p-4 rounded-2xl text-left transition-all duration-200"
              style={{
                background: statusFilter === s ? (s === 'all' ? 'rgba(34,197,94,0.1)' : STATUS_COLORS[s]?.bg) : 'var(--card)',
                border: statusFilter === s ? `1px solid ${s === 'all' ? 'rgba(34,197,94,0.3)' : STATUS_COLORS[s]?.border}` : '1px solid var(--border)',
              }}>
              <div className="text-2xl font-bold mb-1" style={{ color: s === 'all' ? 'var(--primary)' : (STATUS_COLORS[s]?.text || 'var(--foreground)') }}>
                {counts[s]}
              </div>
              <div className="text-xs font-semibold capitalize" style={{ color: 'var(--muted-foreground)' }}>
                {t(`admin_${s}` as any)}
              </div>
            </button>
          ))}
        </div>

        {/* Advanced Moderation Toolbar */}
        <div className="card-glass p-4 rounded-2xl mb-6" style={{ border: '1px solid rgba(34,197,94,0.1)' }}>
          <div className="flex flex-col gap-3">
            {/* Search + Sort */}
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--muted-foreground)' }} />
                <input type="text" value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
                  placeholder={t('admin_search')}
                  className="w-full pl-9 pr-4 py-2.5 rounded-xl text-sm outline-none"
                  style={{ background: 'var(--background)', border: '1px solid var(--border)', color: 'var(--foreground)' }} />
              </div>
              <div className="flex gap-2">
                <button onClick={() => setSortOrder(o => o === 'newest' ? 'oldest' : 'newest')}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 hover:bg-white/5"
                  style={{ background: 'var(--background)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                  <Clock size={14} />
                  {sortOrder === 'newest' ? 'Newest' : 'Oldest'}
                </button>
                <button onClick={loadSubmissions} disabled={dataLoading}
                  className="flex items-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200 hover:bg-white/5"
                  style={{ background: 'var(--background)', border: '1px solid var(--border)', color: 'var(--foreground)' }}>
                  <RefreshCw size={14} className={dataLoading ? 'animate-spin' : ''} />
                  {t('admin_refresh')}
                </button>
              </div>
            </div>

            {/* Category Filter */}
            <div className="flex flex-wrap gap-2">
              <button onClick={() => setCategoryFilter('all')}
                className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                style={{ background: categoryFilter === 'all' ? 'rgba(34,197,94,0.15)' : 'var(--background)', color: categoryFilter === 'all' ? 'var(--primary)' : 'var(--muted-foreground)', border: categoryFilter === 'all' ? '1px solid rgba(34,197,94,0.3)' : '1px solid var(--border)' }}>
                All Categories
              </button>
              {CATEGORIES.map(cat => (
                <button key={cat.id} onClick={() => setCategoryFilter(cat.id)}
                  className="px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                  style={{ background: categoryFilter === cat.id ? `${cat.color}18` : 'var(--background)', color: categoryFilter === cat.id ? cat.color : 'var(--muted-foreground)', border: categoryFilter === cat.id ? `1px solid ${cat.color}40` : '1px solid var(--border)' }}>
                  {cat.icon} {cat.label}
                </button>
              ))}
            </div>

            {/* Bulk Actions */}
            {filtered.length > 0 && (
              <div className="flex flex-wrap items-center gap-3 pt-2" style={{ borderTop: '1px solid var(--border)' }}>
                <button onClick={selectAll}
                  className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:bg-white/5"
                  style={{ background: 'var(--background)', border: '1px solid var(--border)', color: 'var(--muted-foreground)' }}>
                  <input type="checkbox" readOnly checked={bulkSelected.size === filtered.length && filtered.length > 0} className="w-3 h-3" />
                  {bulkSelected.size === filtered.length && filtered.length > 0 ? 'Deselect All' : 'Select All'}
                </button>
                {bulkSelected.size > 0 && (
                  <>
                    <span className="text-xs font-semibold" style={{ color: 'var(--primary)' }}>{bulkSelected.size} selected</span>
                    <button onClick={handleBulkApprove} disabled={bulkLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                      style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}>
                      {bulkLoading ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle size={12} />}
                      Bulk Approve
                    </button>
                    <button onClick={handleBulkReject} disabled={bulkLoading}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200"
                      style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                      {bulkLoading ? <Loader2 size={12} className="animate-spin" /> : <XCircle size={12} />}
                      Bulk Reject
                    </button>
                    <button onClick={() => setBulkSelected(new Set())}
                      className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-200 hover:bg-white/5"
                      style={{ color: 'var(--muted-foreground)', border: '1px solid var(--border)' }}>
                      Clear
                    </button>
                  </>
                )}
                <span className="ml-auto text-xs" style={{ color: 'var(--muted-foreground)' }}>
                  Showing {filtered.length} of {submissions.length}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Submissions List */}
        {dataLoading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 size={32} className="animate-spin" style={{ color: 'var(--primary)' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20">
            <Flame size={40} className="mx-auto mb-4 opacity-30" style={{ color: 'var(--muted-foreground)' }} />
            <p className="font-semibold" style={{ color: 'var(--foreground)' }}>{t('admin_no_submissions')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map(sub => {
              const cat = CATEGORIES.find(c => c.id === sub.category);
              const isExpanded = expandedId === sub.id;
              const statusStyle = STATUS_COLORS[sub.status] || STATUS_COLORS.pending;
              const isSelected = bulkSelected.has(sub.id);

              return (
                <div key={sub.id} className="rounded-2xl overflow-hidden transition-all duration-200"
                  style={{ background: 'var(--card)', border: isSelected ? '1px solid rgba(34,197,94,0.4)' : '1px solid var(--border)', boxShadow: isSelected ? '0 0 0 2px rgba(34,197,94,0.1)' : undefined }}>
                  <div className="p-4 sm:p-5">
                    <div className="flex items-start justify-between gap-3">
                      {/* Bulk checkbox */}
                      <button onClick={() => toggleBulkSelect(sub.id)} className="mt-1 flex-shrink-0">
                        <div className="w-4 h-4 rounded border-2 flex items-center justify-center transition-all duration-150"
                          style={{ borderColor: isSelected ? 'var(--primary)' : 'var(--border)', background: isSelected ? 'var(--primary)' : 'transparent' }}>
                          {isSelected && <Check size={10} className="text-white" />}
                        </div>
                      </button>

                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <div className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5"
                          style={{ background: cat ? `${cat.color}18` : 'rgba(34,197,94,0.1)' }}>
                          {sub.mode === 'screenshot' ? (
                            <ImageIcon size={16} style={{ color: cat?.color || 'var(--primary)' }} />
                          ) : (
                            <span className="text-base">{cat?.icon || '🔥'}</span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex flex-wrap items-center gap-2 mb-2">
                            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                              style={{ background: statusStyle.bg, color: statusStyle.text, border: `1px solid ${statusStyle.border}` }}>
                              {sub.status}
                            </span>
                            <span className="text-xs px-2 py-0.5 rounded-full font-semibold"
                              style={{ background: cat ? `${cat.color}18` : 'rgba(34,197,94,0.1)', color: cat?.color || 'var(--primary)', border: `1px solid ${cat ? cat.color + '30' : 'rgba(34,197,94,0.2)'}` }}>
                              {cat?.label || sub.category}
                            </span>
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>🌍 {sub.country}</span>
                            <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                              {new Date(sub.submittedAt).toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </div>
                          {sub.mode === 'text' && sub.roastText && (
                            <p className="text-sm leading-relaxed" style={{ color: 'var(--foreground)' }}>
                              {isExpanded ? sub.roastText : sub.roastText.slice(0, 120) + (sub.roastText.length > 120 ? '...' : '')}
                            </p>
                          )}
                          {sub.mode === 'screenshot' && sub.screenshotUrl && (
                            <div className="mt-2">
                              {isExpanded ? (
                                <img src={sub.screenshotUrl} alt="Roast screenshot" className="rounded-xl max-w-full max-h-80 object-contain" style={{ border: '1px solid var(--border)' }} />
                              ) : (
                                <div className="flex items-center gap-2 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                                  <ImageIcon size={14} />
                                  {t('admin_screenshot')}
                                </div>
                              )}
                            </div>
                          )}
                          {sub.context && isExpanded && (
                            <p className="text-xs mt-2 italic" style={{ color: 'var(--muted-foreground)' }}>{t('admin_context')}: {sub.context}</p>
                          )}
                        </div>
                      </div>
                      <button onClick={() => setExpandedId(isExpanded ? null : sub.id)}
                        className="p-1.5 rounded-lg hover:bg-white/5 transition-colors flex-shrink-0"
                        style={{ color: 'var(--muted-foreground)' }}>
                        <ChevronDown size={16} className={`transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
                      </button>
                    </div>

                    {isExpanded && (
                      <div className="mt-4 pt-4" style={{ borderTop: '1px solid var(--border)' }}>
                        <div className="mb-3">
                          <label className="block text-xs font-semibold mb-1.5" style={{ color: 'var(--muted-foreground)' }}>{t('admin_notes')}</label>
                          <textarea
                            value={adminNotes[sub.id] || sub.adminNotes || ''}
                            onChange={e => setAdminNotes(prev => ({ ...prev, [sub.id]: e.target.value }))}
                            placeholder={t('admin_notes_placeholder')}
                            rows={2}
                            className="w-full px-3 py-2 rounded-xl text-sm outline-none resize-none"
                            style={{ background: 'var(--background)', border: '1px solid var(--border)', color: 'var(--foreground)' }}
                          />
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {sub.status !== 'approved' && (
                            <button onClick={() => handleUpdateStatus(sub.id, 'approved')} disabled={actionLoading === sub.id + 'approved'}
                              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200"
                              style={{ background: 'rgba(34,197,94,0.1)', color: '#22c55e', border: '1px solid rgba(34,197,94,0.3)' }}>
                              {actionLoading === sub.id + 'approved' ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} />}
                              {t('admin_approve')}
                            </button>
                          )}
                          {sub.status !== 'rejected' && (
                            <button onClick={() => handleUpdateStatus(sub.id, 'rejected')} disabled={actionLoading === sub.id + 'rejected'}
                              className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200"
                              style={{ background: 'rgba(245,158,11,0.1)', color: '#f59e0b', border: '1px solid rgba(245,158,11,0.3)' }}>
                              {actionLoading === sub.id + 'rejected' ? <Loader2 size={14} className="animate-spin" /> : <XCircle size={14} />}
                              {t('admin_reject')}
                            </button>
                          )}
                          <button onClick={() => handleDelete(sub.id)} disabled={actionLoading === sub.id + 'delete'}
                            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all duration-200 ml-auto"
                            style={{ background: 'rgba(239,68,68,0.1)', color: '#ef4444', border: '1px solid rgba(239,68,68,0.3)' }}>
                            {actionLoading === sub.id + 'delete' ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                            {t('admin_delete')}
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
